import { setMilliseconds, sub } from 'date-fns';
import { extractDurationFromNowMinus, isNowValue } from './flexible-time';
import {
  type AbsoluteTimeRange,
  DurationString,
  isDurationString,
  parseDurationString,
  TimeRangeValue,
} from './time-range';
/**
 * Represents a time range from user input that is typically only used when getting or displaying user input for a time
 * range (e.g. the time range picker).
 */
export interface TimeRangeInputValue {
  start: Date | string;
  end: Date | string;
}

/**
 * Converts a `TimeRangeInputValue` to an `AbsoluteTimeRange`. Optionally, you can specify the `Date` used for 'now'.
 */
export function timeRangeInputValueToAbsolute(timeRange: TimeRangeInputValue, now?: Date): AbsoluteTimeRange {
  const { start, end } = timeRange;

  // Mimic what 'toAbsoluteTimeRange` does by setting ms to 0 (see that function for details)
  now ??= setMilliseconds(new Date(), 0);

  return {
    start: typeof start === 'string' ? getDateFromRelativeTimeValue(start, now) : start,
    end: typeof end === 'string' ? getDateFromRelativeTimeValue(end, now) : end,
  };
}

// Convert 'now' or 'now-x' to a date using the `now` argument supplied as the actual Date representing 'now'
function getDateFromRelativeTimeValue(value: string, now: Date) {
  if (isNowValue(value)) {
    return now;
  }

  // TODO: Can we avoid using `null` for this? (we typically prefer `undefined` in the codebase)
  const maybeDuration = extractDurationFromNowMinus(value);
  if (maybeDuration === null) {
    throw new Error('Invalid or unsupported relative time value string');
  }

  const duration = parseDurationString(maybeDuration);
  return sub(now, duration);
}

/**
 * Basically a clone function for a `TimeRangeInputValue`.
 */
export function duplicateTimeRangeInputValue(timeRange: TimeRangeInputValue): TimeRangeInputValue {
  const { start, end } = timeRange;

  return {
    start: typeof start === 'string' ? start : new Date(start),
    end: typeof end === 'string' ? end : new Date(end),
  };
}

/**
 * Returns true if the given `TimeRangeInputValue` is an `AbsoluteTimeRange`.
 */
export function isTimeRangeInputValueAbsolute(timeRange: TimeRangeInputValue): timeRange is AbsoluteTimeRange {
  // Use typeof instead of instanceof Date check to make this also work in Jest tests
  return typeof timeRange.start === 'object' && typeof timeRange.end === 'object';
}

/**
 * An equality function for two `TimeRangeInputValue` instances.
 */
export function areTimeRangeInputValuesEqual(first: TimeRangeInputValue, second: TimeRangeInputValue) {
  return areTimeValuesEqual(first.start, second.start) && areTimeValuesEqual(first.end, second.end);
}

function areTimeValuesEqual(first: Date | string, second: Date | string) {
  if (typeof first === 'string' && typeof second === 'string') {
    return first === second;
  }

  if (first instanceof Date && second instanceof Date) {
    return first.getTime() === second.getTime();
  }

  return false;
}

/**
 * Converts from the old `TimeRangeValue` format to the new `TimeRangeInputValue` format.
 */
export function legacyTimeRangeToTimeRangeInputValue(timeRange: TimeRangeValue): TimeRangeInputValue {
  // Convert relative
  if ('pastDuration' in timeRange) {
    return getTimeRangeFromDurationString(timeRange.pastDuration);
  }

  // Convert absolute
  return { start: timeRange.start, end: timeRange.end };
}

/**
 * Converts from the new `TimeRangeInputValue` format back to the old `TimeRangeValue` format.
 */
export function timeRangeInputValueToLegacyTimeRange(timeRangeInputValue: TimeRangeInputValue): TimeRangeValue {
  // Absolute time
  if (isTimeRangeInputValueAbsolute(timeRangeInputValue)) {
    return { start: timeRangeInputValue.start, end: timeRangeInputValue.end };
  }

  const { start, end } = timeRangeInputValue;

  // New relative time format that we currently support
  if (typeof start === 'string' && typeof end === 'string') {
    if (isNowValue(end) === false) {
      throw new Error(`Cannot convert TimeRangeInputValue to TimeRangeValue. Invalid end value of '${end}'`);
    }

    const duration = extractDurationFromNowMinus(start);
    if (duration === null || isDurationString(duration) === false) {
      throw new Error(`Cannot convert TimeRangeInputValue to TimeRangeValue. Invalid start value of '${start}'`);
    }

    return { pastDuration: duration };
  }

  // This is basically for anything that might mix Date and string values, since we can't represent that in the old
  // TimeRangeValue format
  throw new Error('Cannot convert TimeRangeInputValue to TimeRangeValue');
}

/**
 * Given a `TimeRangeInputValue`, gets the `DurationString` from the `start` value if it's a 'now-xx' string. If the
 * input value is absolute or has an invalid string value, returns `undefined`.
 */
export function getDurationFromTimeRangeStart(timeRange: TimeRangeInputValue): DurationString | undefined {
  const { start } = timeRange;

  // If an absolute time range, just bail
  if (typeof start === 'object') {
    return undefined;
  }

  if (start.startsWith('now-') === false || start.length <= 4) {
    return undefined;
  }

  const maybeDuration = start.substring(4);
  return isDurationString(maybeDuration) ? maybeDuration : undefined;
}

/**
 * Turns a `DurationString` that's in the past into a relative `TimeRangeInputValue`.
 */
export function getTimeRangeFromDurationString(durationString: DurationString): TimeRangeInputValue {
  return { start: `now-${durationString}`, end: 'now' };
}
